import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import MomentUtils from "@date-io/moment";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment";
import { X } from "phosphor-react";
import React, { useState } from "react";
import { fpost, fpatch } from "../../../API/callsAPI";

const EXPIRY_PRESETS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "No expiry", days: null },
  { label: "Custom", days: "custom" },
];

const fieldStyle = {
  backgroundColor: "#252A38",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input": { color: "#F9F9F9", padding: "10px 14px" },
};

const labelStyle = {
  fontSize: "14px",
  color: "#606479",
  marginBottom: "8px",
};


const AddApiKeyModal = ({ handleClose, setSnackValue, editMode = false, initialData = null, updateRefreshCounter }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [selectedPreset, setSelectedPreset] = useState(initialData?.selectedPreset || "30 days");
  const [selectedDate, setSelectedDate] = useState(
    initialData?.customDate ? moment(initialData.customDate, "DD/MM/YYYY") : moment().add(30, "days")
  );

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.label);
    if (preset.days !== "custom" && preset.days !== null) {
      setSelectedDate(moment().add(preset.days, "days"));
    } else if (preset.days === null) {
      setSelectedDate(null);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedPreset("Custom");
  };

  const handleSubmitNewApiKey = async () => {
    try {
      const data = await fpost({
        url: "api-key/",
        data: {
          name: name,
          expires: selectedDate?.format("YYYY-MM-DD"),
        },
      });
      if (data.status === 200 || data.status === 201) {
        setSnackValue({
          isOpen: true,
          message: editMode ? "API Key updated successfully" : "API Key generated successfully",
          isError: false,
        });
      }
    } catch (error) {
      setSnackValue({
        isOpen: true,
        message: `${error}`,
        isError: true,
      });
    } finally {
      updateRefreshCounter();
      handleClose();
    }
  };

  const handleSubmitEditApiKey = async () => {
    try {
      const data = await fpatch({
        url: `api-key/${initialData.uuid}/`, data: {
          name: name,
          expires: selectedDate?.format("YYYY-MM-DD"),
        }
      });
      if (data.status === 200 || data.status === 201) {
        setSnackValue({
          isOpen: true,
          message: "API Key updated successfully",
          isError: false,
        });
      }
    } catch (error) {
      setSnackValue({
        isOpen: true,
        message: `${error}`,
        isError: true,
      });
    } finally {
      updateRefreshCounter();
      handleClose();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#1B1D28",
        borderRadius: "20px",
        width: "600px",
        padding: "24px",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <Typography sx={{ fontSize: "24px", fontWeight: 500, color: "#F9F9F9" }}>
          {editMode ? "Edit API Key" : "New API Key"}
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#B1B5C9" }}>
          <X size={32} />
        </IconButton>
      </Box>

      {/* Name */}
      <Box sx={{ marginBottom: "16px" }}>
        <Typography sx={labelStyle}>
          Name<span style={{ color: "#E41F1F" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="Give this API key a name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          size="small"
          sx={fieldStyle}
          InputProps={{
            sx: {
              backgroundColor: "#252A38",
              borderRadius: "10px",
              "& fieldset": { border: "none" },
              color: "#F9F9F9",
            },
          }}
          inputProps={{ style: { color: "#F9F9F9" } }}
        />
      </Box>

      {/* Expiry */}
      <Box sx={{ marginBottom: "16px" }}>
        <Typography sx={labelStyle}>Expiry</Typography>
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
          <KeyboardDatePicker
            disableToolbar
            disabled={selectedPreset === "No expiry"}
            invalidDateMessage="Invalid date"
            variant="inline"
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
            value={selectedPreset === "No expiry" ? null : selectedDate}
            onChange={handleDateChange}
            minDate={moment()}
            fullWidth
            size="small"
            InputProps={{ disableUnderline: true }}
            KeyboardButtonProps={{
              "aria-label": "change date",
              style: { padding: "4px", color: "#B1B5C9" },
            }}
            inputProps={{ style: { color: "#F9F9F9", padding: "10px 14px" } }}
            style={{
              backgroundColor: "#252A38",
              borderRadius: "10px",
              marginBottom: "10px",
              width: "100%",
            }}
          />
        </MuiPickersUtilsProvider>
        {/* Preset chips */}
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {EXPIRY_PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              onClick={() => handlePresetSelect(preset)}
              sx={{
                backgroundColor:
                  selectedPreset === preset.label ? "#6C65D9" : "#252A38",
                color: selectedPreset === preset.label ? "#F9F9F9" : "#B1B5C9",
                borderRadius: "8px",
                fontFamily: "Poppins",
                fontSize: "13px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    selectedPreset === preset.label ? "#5a54c7" : "#313547",
                },
                "& .MuiChip-label": { padding: "0 12px" },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Submit button */}
      <Button
        fullWidth
        variant="contained"
        onClick={editMode ? handleSubmitEditApiKey : handleSubmitNewApiKey}
        disabled={!name.trim()}
        sx={{
          color: "white",
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "14px",
          padding: "10px 22px",
          bgcolor: name.trim() ? "#6C65D9" : "#252A38",
          "&:hover": { bgcolor: name.trim() ? "#5a54c7" : "#252A38" },
          "&.Mui-disabled": { color: "#606479" },
        }}
      >
        {editMode ? "Save Changes" : "Generate API Key"}
      </Button>
    </Box>
  );
};

export default AddApiKeyModal;
